"use client";

import { type Locale, locales } from "@/data/locales";
import { ArrowRight, ChevronDown, Globe, Languages, X } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

export function LanguageSwitcher({
  locale,
  slug,
  availableLocales,
  className,
}: {
  locale: Locale;
  slug?: string;
  availableLocales?: Locale[];
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const selectableLocales =
    availableLocales && availableLocales.length > 0 ? availableLocales : [...locales];
  const otherLocales = selectableLocales.filter((l) => l !== locale);
  const makeHref = (l: Locale) => {
    if (l === "en") {
      return slug ? `/blog/${slug}` : "/blog";
    }
    return slug ? `/${l}/blog/${slug}` : `/${l}/blog`;
  };

  return (
    <div ref={ref} className={`relative ${className ?? ""}`}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-gray-300 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-orange-500/40"
      >
        <Globe className="h-4 w-4" />
        <span className="font-medium">{locale.toUpperCase()}</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Languages"
          className="absolute right-0 z-50 mt-2 w-64 rounded-xl border border-white/10 bg-[#0f1629] p-2 shadow-xl"
        >
          <div className="px-2 py-1.5 text-xs uppercase tracking-wider text-gray-400 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Languages className="h-3.5 w-3.5" /> Languages
            </span>
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              className="rounded p-1 text-gray-400 hover:text-gray-200 hover:bg-white/5"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-1 grid grid-cols-2 gap-1">
            {otherLocales.map((l) => (
              <Link
                key={l}
                href={makeHref(l)}
                className="flex items-center justify-between rounded-lg px-3 py-2 text-sm text-gray-300 hover:bg-white/5 hover:text-white"
                onClick={() => setOpen(false)}
              >
                <span className="font-medium">{l.toUpperCase()}</span>
                <ArrowRight className="h-4 w-4 opacity-60" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
